import React, { useState, useEffect, useContext } from 'react';
import PageContent from '../../common/MainLayout/Page';
import { useQuery } from '@apollo/client';
import { MANAGE_CLINIC_DETAILS_QUERY } from '../gql';
import { isEmpty, omit } from 'lodash';
import {
  Stack,
  Tabs,
  Tab,
  Box,
} from '@mui/material';
import User from '../../auth/models/User';
import EditClinic from './EditClinic/EditClinic';
import ClinicUsersTable from './ClinicDetails/ClinicUsersTable';
import ClinicProvidersTable from './ClinicDetails/ClinicProvidersTable';
import { ClinicProvider } from '../context/ClinicContext';
import clientGraphql, { useMutation } from '../../../common/apollo-graphql';
import AuthContext from '../../auth/context/AuthContext';
import ManageReferralFormClinicDetails from './ManageReferralFormClinicDetails';
import { SAVE_CLINIC_MINIMAL_MUTATION } from '../gql/Mutation';
import {ClinicTagsAutocompleteProvider} from '../components/ClinicTagsHookFormAutocomplete';
import LabeledSwitch from '../../common/components/LabeledSwitch';

function ViewClinicDetails({ clinicData, userData, providerData, loading }) {
  const [isOrder, setIsOrder] = useState(clinicData.is_order);
  const [debouncedValue, setDebouncedValue] = useState(isOrder);
  const [localClinicData, setLocalClinicData] = useState(clinicData);

  const [saveClinic, { loading: saveClinicLoading }] = useMutation(
      SAVE_CLINIC_MINIMAL_MUTATION,
      { client: clientGraphql }
  );

  useEffect(() => {
    setDebouncedValue(isOrder);
  }, [isOrder]);

  useEffect(() => {
    if (debouncedValue !== localClinicData.is_order) {
      saveClinic({
        variables: {
          clinic: {
            id: localClinicData.id,
            name: localClinicData.name,
            is_order: debouncedValue,
          }
        },
      }).then(() => {
        setLocalClinicData({
          ...localClinicData,
          is_order: debouncedValue
        })
      });
    }
  }, [debouncedValue, localClinicData, saveClinic]);


  const onChange = (val) => {
    setIsOrder(val === true || val === "true");
  };

  return (
      <Stack direction="column" spacing={1}>
        <LabeledSwitch
          header="Referral Type"
          subheader="Does this clinic only receive orders?"
          withSwitchLabel={true}
          checked={isOrder}
          onChange={(e) => onChange(e.target.checked)}
          disabled={saveClinicLoading}
        />
        <br /><br />
        <ClinicUsersTable
            data={userData}
            providers={!isEmpty(providerData) ? providerData.rows : []}
            loading={loading}
        />
        <ClinicProvidersTable data={providerData} loading={loading} />
      </Stack >
  );
}

export default function ManageClinicDetails({ match }) {
  const currentUser = new User(useContext(AuthContext));

  const [userData, setUserData] = useState({ rows: [], count: 0 });
  const [providerData, setProviderData] = useState({ rows: [], count: 0 });
  const [tab, setTab] = useState(0);

  const { id } = match.params;
  const { loading, data, refetch } = useQuery(MANAGE_CLINIC_DETAILS_QUERY, {
    client: clientGraphql,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { id: id },
  });

  useEffect(() => {
    if (data) {
      const { clinic } = data;
      let newData = omit(clinic, [
        'id',
        '__typename',
        'aggr_users',
        'address_json',
        'full_address',
        'documents',
        'specializations',
        'users',
        'required_documents',
        'optional_documents',
        'providers',
      ]);
      newData['office_phone'] = '';
      newData['fax'] = '';

      if ('extra_info' in newData && !isEmpty(newData['extra_info'])) {
        const extra_info = JSON.parse(JSON.parse(newData['extra_info']));
        newData['office_phone'] =
            'office_phone' in extra_info ? extra_info['office_phone'] : '';
        newData['fax'] = 'fax' in extra_info ? extra_info['fax'] : '';
      }
      if ('extra_info' in newData) {
        newData = omit(newData, ['extra_info']);
      }

      const rows = [];
      data.clinic.users.forEach((item) => {
        if (item.user != null) {
          rows.push({
            is_primary: item.is_primary,
            clinicId: data.clinic.id,
            ...item.user,
            userObj: new User({ ...item.user, is_primary: item.is_primary }),
          });
        }
      });

      setUserData({
        rows: rows,
        count: rows.length,
      });
      setProviderData({
        rows: data.clinic.providers.map((item) => {
          return {
            ...item,
          };
        }),
        count: data.clinic.providers.count,
      });
    }
  }, [data]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  return (
      <PageContent
          contentClass="management-manage-clinic-details"
          loading={loading}
          isBreadcrumbs={currentUser.isAdmin()}
          contentSX={{ background: 'transparent', px: 4 }}
      >
        <ClinicProvider
            value={{
              id,
              refetch,
            }}
        >
          <ClinicTagsAutocompleteProvider tags={data?.clinic?.tags}>
            <Box sx={{ mb: 4 }}>
              <Tabs
                  value={tab}
                  onChange={handleTabChange}
                  variant="fullWidth"
                  TabIndicatorProps={{ style: { display: 'none' } }}
                  sx={{
                    border: 'none',
                    p: 0,
                    m: 0,
                    mt: -2,
                    mb: 4,
                    '& .MuiTabs-flexContainer': {
                      gap: 0,
                      py: 0,
                      borderRadius: 2,
                      background: '#f5f5f5',
                    },
                    '& .MuiTab-root': {
                      minHeight: '36px',
                      textTransform: 'none',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      color: '#555',
                      letterSpacing: '0.3px',
                      borderRadius: '6px',
                      transition: 'all 0.2s',
                      border: 'none',
                      outline: 'none',
                      boxShadow: 'none',
                      flex: 1,
                      maxWidth: 'none',
                      padding: '0px',
                      my: '2px'
                    },
                    '& .MuiTab-root:hover': {
                      backgroundColor: '#e0e0e0',
                      color: '#1976d2',
                    },
                    '& .Mui-selected': {
                      backgroundColor: '#fff',
                      color: '#1976d2',
                      fontWeight: 700,
                      boxShadow: 1,
                    },
                  }}
              >
                <Tab label="Details" />
                <Tab label="Configure" />
                <Tab label="Forms" />
              </Tabs>

            </Box>

            <Box sx={{ p: 6, background: 'white', boxShadow: 3, borderRadius: 2 }}>
              {tab === 0 && (
                  <EditClinic loading={loading} data={data} />
              )}

              {tab === 1 && (
                  <ViewClinicDetails
                      clinicData={data.clinic}
                      loading={loading}
                      userData={userData}
                      providerData={providerData}
                  />
              )}

              {tab === 2 && (
                  <ManageReferralFormClinicDetails id={id} />
              )}
            </Box>
          </ClinicTagsAutocompleteProvider>
        </ClinicProvider>
      </PageContent>
  );
}
